import React, { useEffect, useState, useRef } from 'react';
import { useEditorStore } from '../../store/editorStore';

interface TooltipState {
  x: number;
  y: number;
  visible: boolean;
}

export const TooltipOverlay: React.FC<{ stageRef: React.RefObject<any> }> = () => {
  const hoveredId = useEditorStore(s => s.hoveredId);
  const racks = useEditorStore(s => s.racks);
  const telemetryData = useEditorStore(s => s.telemetryData);
  const [pos, setPos] = useState<TooltipState>({ x: 0, y: 0, visible: false });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hoveredId) {
      setPos(p => ({ ...p, visible: false }));
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const parent = containerRef.current?.parentElement || document.querySelector('.konvajs-content')?.parentElement;
      if (!parent) {
        setPos(p => ({ ...p, x: e.clientX + 16, y: e.clientY - 8, visible: true }));
        return;
      }
      const rect = parent.getBoundingClientRect();
      setPos({
        x: e.clientX - rect.left + 16,
        y: e.clientY - rect.top - 8,
        visible: true,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [hoveredId]);

  if (!hoveredId || !pos.visible) return null;

  const rack = racks[hoveredId];
  const tele = telemetryData[hoveredId];
  if (!rack) return null;

  const iconMap: Record<string, string> = {
    server_rack: 'Серверная стойка',
    network_rack: 'Сетевой шкаф',
    open_frame: 'Открытая стойка',
    crac: 'Кондиционер (CRAC)',
    inrow_cooler: 'Межрядный кондиционер (In-Row)',
    ups: 'Источник бесперебойного питания (ИБП)',
    pdu_frame: 'Модуль распределения питания (Floor PDU)',
    pillar: 'Несущая колонна здания',
  };

  const isFacility = Boolean(rack.isFacility || ['crac', 'inrow_cooler', 'ups', 'pdu_frame'].includes(rack.rackType));
  const isPillar = Boolean(rack.isBlocked || rack.rackType === 'pillar');

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        left: Math.min(pos.x, window.innerWidth - 220),
        top: pos.y,
        pointerEvents: 'none',
        zIndex: 100,
        animation: 'fadeIn 0.12s ease',
      }}
    >
      <div style={{
        background: 'rgba(10, 15, 28, 0.95)',
        border: '1px solid #2a3a5a',
        borderRadius: 8,
        padding: '10px 14px',
        minWidth: 190,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        backdropFilter: 'blur(8px)',
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
        color: '#f3f4f6',
      }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: isPillar ? '#a1a1aa' : isFacility ? '#c084fc' : '#60a5fa' }}>
          {rack.label}
        </div>
        <div style={{ color: '#9ca3af', marginBottom: 8, fontSize: 11 }}>
          {iconMap[rack.rackType] || rack.rackType}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 12px' }}>
          {isPillar ? (
            <>
              <span style={{ color: '#71717a' }}>Тип</span>
              <span style={{ color: '#a1a1aa', textAlign: 'right', fontWeight: 600 }}>Архитектура</span>
              <span style={{ color: '#71717a' }}>Монтаж</span>
              <span style={{ color: '#ef4444', textAlign: 'right' }}>Заблокирован</span>
            </>
          ) : isFacility ? (
            <>
              <span style={{ color: '#6b7280' }}>Назначение</span>
              <span style={{ color: '#a855f7', textAlign: 'right', fontWeight: 600 }}>Инфраструктура</span>
              <span style={{ color: '#6b7280' }}>IT-ёмкость</span>
              <span style={{ color: '#9ca3af', textAlign: 'right' }}>0U (Facility)</span>
            </>
          ) : (
            <>
              <span style={{ color: '#6b7280' }}>U-слотов</span>
              <span style={{ color: '#e2e8f0', textAlign: 'right' }}>{rack.uCount ?? 42}U</span>
            </>
          )}
          <span style={{ color: '#6b7280' }}>Габариты</span>
          <span style={{ color: '#e2e8f0', textAlign: 'right' }}>
            {(rack.widthM || 0.6).toFixed(1)} × {(rack.depthM || 1.0).toFixed(1)} м
          </span>
          <span style={{ color: '#6b7280' }}>X / Y</span>
          <span style={{ color: '#e2e8f0', textAlign: 'right' }}>
            {(rack.x / 100).toFixed(2)} / {(rack.y / 100).toFixed(2)} м
          </span>
          {tele && (
            <>
              {tele.temperature !== undefined && (
                <>
                  <span style={{ color: '#6b7280' }}>Температура</span>
                  <span style={{
                    color: tele.temperature > 35 ? '#ef4444' : tele.temperature > 28 ? '#f97316' : '#22c55e',
                    textAlign: 'right', fontWeight: 600
                  }}>
                    {tele.temperature.toFixed(1)} °C
                  </span>
                </>
              )}
              {tele.usedU !== undefined && !isFacility && !isPillar && (
                <>
                  <span style={{ color: '#6b7280' }}>Занято юнитов</span>
                  <span style={{
                    color: (tele.usedU / (tele.totalU || rack.uCount || 42)) > 0.9 ? '#ef4444' : '#22c55e',
                    textAlign: 'right', fontWeight: 600
                  }}>
                    {tele.usedU} / {tele.totalU || rack.uCount || 42} U ({Math.round((tele.usedU / (tele.totalU || rack.uCount || 42)) * 100)}%)
                  </span>
                  <span style={{ color: '#6b7280' }}>Свободно</span>
                  <span style={{ color: '#38bdf8', textAlign: 'right', fontWeight: 600 }}>
                    {Math.max(0, (tele.totalU || rack.uCount || 42) - tele.usedU)} U
                  </span>
                </>
              )}
              {tele.powerLoad !== undefined && (
                <>
                  <span style={{ color: '#6b7280' }}>Мощность</span>
                  <span style={{
                    color: tele.powerLoad > tele.powerCapacity * 0.9 ? '#ef4444' : '#22c55e',
                    textAlign: 'right', fontWeight: 600
                  }}>
                    {tele.powerLoad.toFixed(1)} / {tele.powerCapacity} кВт
                  </span>
                </>
              )}
              {tele.hasSpof && (
                <span style={{ color: '#facc15', gridColumn: '1 / -1', marginTop: 4 }}>
                  Внимание: Точка единственного отказа {tele.spofReason ? `(${tele.spofReason})` : ''}
                </span>
              )}
              {tele.powerLoad !== undefined && tele.powerCapacity !== undefined && tele.powerLoad > tele.powerCapacity && (
                <span style={{ color: '#ef4444', gridColumn: '1 / -1', marginTop: 4, fontWeight: 'bold' }}>
                  Предупреждение: Перегрузка питания!
                </span>
              )}
              {tele.temperature !== undefined && tele.temperature > 35 && (
                <span style={{ color: '#ef4444', gridColumn: '1 / -1', marginTop: 4, fontWeight: 'bold' }}>
                  Критический нагрев! (&gt;35°C)
                </span>
              )}
            </>
          )}
          {!tele && (
            <span style={{ color: '#4b5563', gridColumn: '1 / -1', fontStyle: 'italic' }}>
              Нет данных телеметрии
            </span>
          )}
          {rack.isDirty && (
            <span style={{ color: '#facc15', gridColumn: '1 / -1', fontSize: 10, marginTop: 4 }}>
              ● Не опубликовано (черновик)
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
